import {
  Button,
  Center,
  CenterProps,
  HStack,
  Icon,
  IconButton,
  Select,
  SkeletonText,
  Spinner,
  Square,
  Stack,
  Text,
  useToast,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { convertFileToBase64 } from '@pelicargo/ui';
import { getPrettyDocumentType } from '@pelicargo/utils';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { HiUpload } from 'react-icons/hi';
import { HiOutlineDocument, HiXMark } from 'react-icons/hi2';
import { useSessionStorage } from 'usehooks-ts';

import { trpc } from '../config/trpc';
import { useDocuments } from '../hooks/documents/useDocuments';
import { RequestFormValues } from '../hooks/request/useForm';

type Props = CenterProps & {
  name?: string;
  documentType?: DocumentType;
  requestId?: number;
  accountId?: number;
  isTypeSelectable?: boolean;
  maxFiles?: number;
};

export const SimpleDropzone = ({
  name,
  documentType,
  requestId,
  accountId,
  isTypeSelectable = false,
  maxFiles = 10,
  ...rest
}: Props) => {
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const [request] = useSessionStorage<RequestFormValues>('request', {} as any);
  const [filesDropped, setFilesDropped] = useState(1);
  const [documents] = useDocuments({ requestId, localName: name });
  const [isDocumentDeleting, setIsDocumentDeleting] = useState<Record<number, boolean>>({});
  const [documentTypes, setDocumentTypes] = useState<Record<number, DocumentType>>({});

  const { setValue, register, watch } = useFormContext();
  register(name);

  const { mutateAsync: uploadDocument, isLoading: isUploading } = trpc.uploadDocument.useMutation();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();

  useEffect(() => {
    const documentTypeEntries = Object.entries(documentTypes);
    if (documentTypeEntries.length) {
      documentTypeEntries.forEach(([id, type]) => {
        const docIndex = documents.findIndex((doc) => doc.id === Number(id));
        if (docIndex !== -1) {
          if (name) setValue(`${name}.${docIndex}`, { ...documents[docIndex], type } as any);
        }
      });
    }
  }, [documentTypes, documents, name, setValue]);

  const onDrop = async (files) => {
    if (documents.length >= maxFiles || files.length > maxFiles) {
      toast({ title: 'Error', description: `You can only upload up to ${maxFiles} files.`, status: 'error' });
      return;
    }
    setFilesDropped(files.length);

    const existingDocuments = watch(name) || [];
    const newDocumentPromises = files.map(uploadFile);
    const newDocuments = await Promise.all(newDocumentPromises);

    if (name) setValue(name, [...(existingDocuments || []), ...newDocuments]);

    setFilesDropped((prev) => Math.max(1, prev - newDocuments.length));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > 5) throw new Error('File size exceeds 5MB. Please choose a smaller file.');

        const newDocument = await uploadDocument({
          content: await convertFileToBase64(file),
          filename: file.name,
          content_type: file.type,
          document_type: documentType,
          request_id: requestId,
          account_id: accountId,
        });

        if (!newDocument?.id) throw new Error('Something went wrong. Please try again.');

        return newDocument;
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      }
    },
    [uploadDocument, documentType, requestId, accountId, toast],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: true }));
        await deleteDocument({ id });
        const nextDocuments = request?.documents?.filter((doc) => doc.id !== id) || [];

        if (name) setValue(name, nextDocuments);
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      } finally {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: false }));
      }
    },
    [deleteDocument, name, request, setValue, toast],
  );

  const inputProps = getInputProps();

  const renderItem = (item: Partial<Document>, index: number) => {
    const value = documentTypes[item?.id!] || item?.type;
    const isInvalidType = isTypeSelectable && value == null;

    return (
      <Stack
        direction="row"
        align="center"
        justify="space-between"
        w="full"
        borderWidth="1px"
        pl="4"
        py="2"
        borderRadius="md"
        key={index}
      >
        <Stack direction="row" align="center">
          <Icon as={HiOutlineDocument} />
          <SkeletonText minW="10" noOfLines={1} isLoaded={!!item?.id}>
            <Show if={isTypeSelectable}>
              <Select
                value={value}
                onChange={(event) => {
                  setDocumentTypes((prev) => ({
                    ...prev,
                    [item?.id!]: event.target.value as DocumentType,
                  }));
                }}
                borderWidth={isInvalidType ? '1px' : undefined}
                borderColor={isInvalidType ? 'red' : undefined}
              >
                <option key={null} value={null}>
                  {null}
                </option>
                {Object.values(DocumentType).map((type) => (
                  <option key={type} value={type}>
                    {getPrettyDocumentType(type)}
                  </option>
                ))}
              </Select>
              <Show if={isInvalidType}>
                <Text color="red" fontSize="xs">
                  Please select a document type.
                </Text>
              </Show>
            </Show>
            <Show if={!isTypeSelectable}>
              <Text w="fit-content">{getPrettyDocumentType(item?.type)}</Text>
            </Show>
          </SkeletonText>
          <SkeletonText minW={item?.original_filename?.length || 32} noOfLines={1} isLoaded={!!item?.id}>
            <Text w="full" noOfLines={1}>
              {item?.original_filename}
            </Text>
          </SkeletonText>
        </Stack>
        <Stack direction="row" align="center" spacing="4">
          <IconButton
            p="0"
            variant="link"
            icon={<HiXMark />}
            isLoading={isDocumentDeleting[item?.id!]}
            aria-label="Remove document"
            onClick={() => handleDelete(item?.id)}
          />
        </Stack>
      </Stack>
    );
  };

  const copy = useMemo(() => {
    return documents?.length > 0 ? 'Click to replace' : 'Click to upload';
  }, [documents?.length]);

  return (
    <Stack>
      <Stack spacing="2" w="full">
        {documents?.map(renderItem)}
        <Show if={isUploading}>
          {Array.from({ length: filesDropped }).map((_, index) => renderItem({} as Document, index))}
        </Show>
      </Stack>
      <Center
        borderWidth="1px"
        borderColor="gray.300"
        borderStyle="dashed"
        borderRadius="lg"
        px="6"
        py="4"
        flexDirection="column"
        w="full"
        {...getRootProps()}
        {...rest}
      >
        <Stack spacing="2" py="2" align="center">
          <VisuallyHiddenInput type="file" display="none" {...omit(inputProps, 'size')} />
          <Show if={!isUploading}>
            <Square size="10" borderRadius="lg">
              <Icon as={HiUpload} boxSize="5" />
            </Square>
          </Show>
          <Show if={isUploading}>
            <Square size="10" borderRadius="lg">
              <Spinner size="sm" />
            </Square>
          </Show>
          <Stack align="center" textAlign="center" spacing="1">
            <HStack spacing="1" align="center" whiteSpace="nowrap">
              <Button variant="link" colorScheme="blue" size="md" p="0">
                {copy}
              </Button>
              <Text fontSize="md">or drag and drop</Text>
            </HStack>
            <Text fontSize="xs">PDFs are recommended.</Text>
          </Stack>
        </Stack>
      </Center>
    </Stack>
  );
};
